import { useEffect, useRef } from "react";
import { useIsomorphicLayoutEffect } from "./useIsomorphicLayoutEffect";

/**
 * `setInterval` as a hook. Pass `delay = null` to pause. Captures the latest
 * callback in a ref so consumers can pass inline functions without re-running
 * the effect on every render.
 *
 *   const [running, setRunning] = useState(true);
 *   useInterval(() => poll(), running ? 5000 : null);
 *
 * Adapted from Dan Abramov's "Making setInterval Declarative" (overreacted.io)
 * and usehooks-ts (MIT).
 */
export function useInterval(callback: () => void, delay: number | null): void {
  const cbRef = useRef(callback);

  useIsomorphicLayoutEffect(() => {
    cbRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (delay === null) return;
    const id = setInterval(() => cbRef.current(), delay);
    return () => clearInterval(id);
  }, [delay]);
}
